/**
 * OrientationCloseManager
 *
 * Long-lived owner of the header close button used to dismiss/pause orientation.
 * Mirrors the PrivacyModeManager pattern: bind the click handler once at app init,
 * delegate visibility to FeatureToggleService via the `toggle:closeButton` event.
 *
 * The dismissal action (API call + currentUser update + event emit + navigation
 * to dashboard) lives here as the single source of truth — both the header
 * button click and the inline "Exit" button in OrientationMode's INTRODUCTION
 * phase delegate to `dismiss()`.
 *
 * Usage:
 *   const manager = new OrientationCloseManager({
 *     closeButton: document.getElementById('header-close-btn')
 *   });
 *   manager.setFeatureToggleService(featureToggleService);
 */

import { API } from '../core/api.js';

export class OrientationCloseManager {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.closeButton - The header close button element
   */
  constructor(options) {
    this.closeButton = options.closeButton;
    this.isFeatureEnabled = false; // Hidden until FeatureToggleService says otherwise

    this.handleClick = this.handleClick.bind(this);
    this.initialize();
  }

  /**
   * Initialize: bind the click handler once. Button visibility is owned by
   * FeatureToggleService and applied through `setFeatureToggleService` /
   * `applyFeatureState`.
   */
  initialize() {
    if (!this.closeButton) {
      console.warn('[OrientationCloseManager] Close button not found');
      return;
    }

    this.closeButton.addEventListener('click', this.handleClick);

    console.log('[OrientationCloseManager] Initialized');
  }

  /**
   * Click handler delegates to the dismiss action.
   * @private
   */
  handleClick() {
    this.dismiss();
  }

  /**
   * Dismiss/pause orientation. Single source of truth for the action.
   *
   *   - Writes `orientationDismissedAt` on the server
   *   - Mirrors it onto `window.app.currentUser` so the router gate and
   *     dashboard card pick it up without a refetch
   *   - Emits `orientation:dismissed`
   *   - Navigates to the dashboard via the existing returnToDashboard sequence
   *
   * Preserves `onboardingStep` so the user can resume from the same phase via
   * the dashboard "Finish Orientation" card. FeatureToggleService keeps the
   * rest of the app locked because `onboardingStep` remains at the phase value.
   *
   * @returns {Promise<boolean>} True if dismissal succeeded
   */
  async dismiss() {
    console.log('[OrientationCloseManager] Dismissing orientation');

    try {
      const result = await API.dismissGuestOnboarding();
      if (window.app?.currentUser) {
        window.app.currentUser.orientationDismissedAt =
          result?.data?.orientationDismissedAt || new Date().toISOString();
      }
    } catch (error) {
      console.error('[OrientationCloseManager] Failed to dismiss orientation:', error);
      return false;
    }

    const eventBus = window.modeOrchestrator?.eventBus;
    if (eventBus) {
      eventBus.emit('orientation:dismissed', {
        onboardingStep: window.app?.currentUser?.onboardingStep || null
      });
    }

    // Return to dashboard using the same sequence as orientation completion.
    // returnToDashboard clears the orchestrator's mode/session state; the
    // pageManager + dashboardManager calls drive the UI swap; pushState
    // canonicalizes the URL.
    if (window.modeOrchestrator) {
      window.modeOrchestrator.returnToDashboard();
    }
    window.app?.modules?.pageManager?.showDashboard?.();
    window.app?.modules?.dashboardManager?.returnToDashboard?.();
    window.history.pushState({}, '', '/');

    return true;
  }

  /**
   * Subscribe to FeatureToggleService visibility events. Follows the
   * PrivacyModeManager wiring contract.
   * @param {FeatureToggleService} service
   */
  setFeatureToggleService(service) {
    if (window.eventBus) {
      window.eventBus.on('toggle:closeButton', ({ visible }) => {
        this.isFeatureEnabled = visible;
        this.applyFeatureState();
      });
    }

    this.isFeatureEnabled = service.get('closeButton');
    this.applyFeatureState();
  }

  /**
   * Apply current feature state to button visibility. Uses `feature-toggle--hidden`
   * to match the convention used by privacy/upgrade buttons.
   * @private
   */
  applyFeatureState() {
    if (!this.closeButton) return;

    if (this.isFeatureEnabled) {
      this.closeButton.classList.remove('feature-toggle--hidden');
      this.closeButton.hidden = false;
    } else {
      this.closeButton.classList.add('feature-toggle--hidden');
      this.closeButton.hidden = true;
    }
  }
}
